import { useContext, createContext } from 'react';
import { BreakpointsContext, BreakpointsContextType } from '@cvent/carina/components/Breakpoints/BreakpointsProvider';

export interface AttendeeHubBreakpoints extends BreakpointsContextType, ContentBreakpoints {
  isMobile?: boolean;
  isLandscapeMobile?: boolean;
  isDesktop?: boolean;
  isMobileOrTablet?: boolean;

  // https://developer.mozilla.org/en-US/docs/Web/CSS/CSS_media_queries/Using_media_queries
  mediaIsDefaultSize: string;
  mediaIsS: string;
  mediaIsM: string;
  mediaIsL: string;
  mediaIsXL: string;

  /** Convenience for both isDefaultSize and isS */
  mediaIsMobile: string;
  /** Convenience for medium and above */
  mediaIsDesktop: string;
  /** Convenience for large and above */
  mediaIsLargeAndAbove: string;
  /** Convenience for small and below */
  mediaIsSmallAndBelow: string;
}

/**
 * Simple hook for getting breakpoint data from BreakpointsContext
 * https://carina-dev.core.cvent.org/components/breakpoints
 */

const useBreakpoints = (): AttendeeHubBreakpoints => {
  const breakpoints = useContext<BreakpointsContextType>(BreakpointsContext);
  const contentBreakpoints = useContext<ContentBreakpoints>(ContentBreakpointsContext);
  return {
    ...contentBreakpoints,
    ...breakpoints,
    isMobile: breakpoints.isDefaultSize || breakpoints.isS,
    isLandscapeMobile: breakpoints.heightWindow < breakpoints.m,
    isDesktop: breakpoints.isM || breakpoints.isL || breakpoints.isXL,
    isMobileOrTablet: breakpoints.isDefaultSize || breakpoints.isS || breakpoints.isM,
    mediaIsDefaultSize: `@media (max-width: ${breakpoints.s - 1}px)`,
    mediaIsS: `@media (min-width: ${breakpoints.s}px) and (max-width: ${breakpoints.m - 1}px)`,
    mediaIsM: `@media (min-width: ${breakpoints.m}px) and (max-width: ${breakpoints.l - 1}px)`,
    mediaIsL: `@media (min-width: ${breakpoints.l}px) and (max-width: ${breakpoints.xl - 1}px)`,
    mediaIsXL: `@media (min-width: ${breakpoints.xl}px)`,
    mediaIsMobile: `@media (max-width: ${breakpoints.m - 1}px)`,
    mediaIsDesktop: `@media (min-width: ${breakpoints.m}px)`,
    mediaIsSmallAndBelow: `@media (max-width: ${breakpoints.m - 1}px)`,
    mediaIsLargeAndAbove: `@media (min-width: ${breakpoints.l}px)`
  };
};

export default useBreakpoints;

interface ContentBreakpoints {
  /**
   * For use when displaying floating content below the header such as toast notifications or placing sticky headers.
   * Do not use for calculating the height of non floating page content. Instead, fill the available space.
   */
  contentTop: number;
}
export const ContentBreakpointsContext = createContext<ContentBreakpoints>({
  contentTop: 88
});
